import { createContext, useContext, useEffect, useMemo, useRef, type PropsWithChildren } from 'react'

// Signed-URL cache scoped to a React subtree so it can never leak across user
// sessions: mount this provider inside the authenticated tree and the cache
// dies with the user on sign-out. Module-scope state was the old shape and
// would survive a sign-out, leaving user A's bearer URLs accessible to user B
// until the per-entry 8-minute timeout fired.

export interface CarouselMediaCache {
  get(key: string): Promise<string> | undefined
  set(key: string, value: Promise<string>): void
  delete(key: string): void
  /** Schedule an entry to be removed after `ttlMs`. Resetting the same key cancels the prior timer. */
  scheduleEviction(key: string, ttlMs: number): void
  clear(): void
}

const createCache = (): CarouselMediaCache => {
  const entries = new Map<string, Promise<string>>()
  const timers = new Map<string, number>()
  const clearTimer = (key: string) => {
    const timer = timers.get(key)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      timers.delete(key)
    }
  }
  return {
    get: (key) => entries.get(key),
    set: (key, value) => {
      entries.set(key, value)
    },
    delete: (key) => {
      clearTimer(key)
      entries.delete(key)
    },
    scheduleEviction: (key, ttlMs) => {
      clearTimer(key)
      timers.set(key, window.setTimeout(() => {
        timers.delete(key)
        entries.delete(key)
      }, ttlMs))
    },
    clear: () => {
      timers.forEach((timer) => window.clearTimeout(timer))
      timers.clear()
      entries.clear()
    },
  }
}

const CarouselCacheContext = createContext<CarouselMediaCache | null>(null)

// Fallback cache for callers (tests, SSR snapshots, isolated previews) that
// don't mount the provider. Reachable via `getDefaultCarouselCache()`; the
// provider-scoped cache always wins inside the React tree.
let defaultCache: CarouselMediaCache | null = null
export const getDefaultCarouselCache = (): CarouselMediaCache => {
  if (!defaultCache) defaultCache = createCache()
  return defaultCache
}

export const CarouselCacheProvider = ({ children }: PropsWithChildren) => {
  const cacheRef = useRef<CarouselMediaCache | null>(null)
  if (!cacheRef.current) cacheRef.current = createCache()
  useEffect(() => () => cacheRef.current?.clear(), [])
  const value = useMemo(() => cacheRef.current as CarouselMediaCache, [])
  return <CarouselCacheContext.Provider value={value}>{children}</CarouselCacheContext.Provider>
}

export const useCarouselCache = (): CarouselMediaCache => {
  const context = useContext(CarouselCacheContext)
  return context ?? getDefaultCarouselCache()
}
